import React, { useState, useContext } from "react";
import AuthLayout from "../../../shared/auth-layout/auth-layout";
import CustomInput from "../../../shared/custom-input/custom-input";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CustomButton from "../../../shared/custom-button/custom-button";
import { useNavigate } from "react-router-dom";
import { loginService, resendConfirmationCode } from "../../../services/auth-service";
import { useDispatch } from "react-redux";
import { logIn } from "../../../redux/store/isLogged-slices";
import { setUserData } from "../../../redux/store/userData-slices";
import { AppDispatch } from "../../../redux/store/store";
import { SnackbarContext } from "../../../config/hooks/use-toast";
import { getLoginError } from "../../../utils/auth-errors";
const Login = () => {
  const navigation = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const snackbarContext = useContext(SnackbarContext);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });
  const [isChecked, setIsChecked] = useState(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    validateField(name, value);
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  const validateField = (name: string, value: string) => {
    let error = "";
  
    switch (name) {
      case "email":
        if (!value) {
          error = "Email est requis.";
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          error = "Email invalide.";
        }
        break;
  
      case "password":
        if (!value) {
          error = "Mot de passe est requis.";
        } else if (
          !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>\/+\\[\]\-])[A-Za-z\d!@#$%^&*(),.?":{}|<>\/+\\[\]\-]{8,}$/.test(value)
        ) {
          error =
            "Les mots de passe doivent comporter au moins 8 caractères et inclure au moins une lettre majuscule, " +
            "une lettre minuscule, un chiffre et un caractère spécial.";
        }
        break;
  
      default:
        break;
    }
  
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
    return error;
  };
  
  

  const validateForm = () => {
    let isValid = true;

    for (const [name, value] of Object.entries(formData)) {
      const error = validateField(name, value as string);
      if (error) {
        isValid = false;
        snackbarContext?.showMessage("Erreur", error, "error");
        break;
      }
    }

    return isValid;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }


    loginService(formData)
      .then((res) => {
        dispatch(logIn());
        if (res.data.userEntityDTO.role.name === "ROLE_TEACHER") {
          navigation("/subject");
        } else {
          navigation("/home");
        }
        if(snackbarContext) {
          snackbarContext.showMessage("Succès", "Vous êtes connecté", "success");
        }
        dispatch(setUserData(res.data.userEntityDTO));
      })
      .catch((e) => {
        const errorMsg = e?.response?.data?.errors?.[0] || "Erreur inconnue";
        const errorMessageField = e?.response?.data?.message || "";
        const isDisabled =
          errorMsg === "Le compte utilisateur est désactivé" ||
          errorMsg === "User is disabled" ||
          errorMessageField.toLowerCase().includes("disabled");
        if (isDisabled) {
          if (snackbarContext) {
            snackbarContext.showMessage("Erreur", "Veuillez confirmer votre email pour activer votre compte", "error");
            // Only redirect if email is present and valid
            if (formData.email && /\S+@\S+\.\S+/.test(formData.email)) {
              // Resend confirmation code automatically
              resendConfirmationCode(formData.email)
                .then(() => {
                  snackbarContext.showMessage("Info", "Un nouveau code de confirmation a été envoyé à votre email", "info");
                })
                .catch(() => {
                  // Silently fail, user can still manually resend from confirm-email page
                });
              setTimeout(() => {
                navigation(`/confirm-email?email=${encodeURIComponent(formData.email)}`);
              }, 2000);
            }
          }
        } else {
          const loginError = getLoginError(e);
          if (loginError.field) {
            setErrors((prevErrors) => ({
              ...prevErrors,
              [loginError.field as "email" | "password"]: loginError.message,
            }));
          }
          snackbarContext?.showMessage("Erreur", loginError.message, "error");
        }
      });



  };

  return (
    <AuthLayout
      title1={"Bienvenue à"}
      title2={"Fok Nje7ek"}
      title3={"Plateforme d'apprentissage"}
    >
      <div className="auth-form-header">
        <h2 className="auth-form-title">Se connecter</h2>
        <p className="auth-form-subtitle">Connectez-vous à votre compte pour continuer</p>
      </div>
      
      <form className="w-full mt-8" >
        <div className="space-y-5">
          <CustomInput
            label={"Email"}
            inputType="email"
            iconPrefix={<MailOutlineOutlinedIcon className="text-title" />}
            placeholder={"Votre E-mail"}
            CustomStyle={""}
            value={formData.email}
            name="email"
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            error={!!errors.email}
            errorMessage={errors.email}
          />
          <CustomInput
            label="Mot de passe"
            inputType={"password"}
            placeholder={"Votre Mot de passe"}
            CustomStyle={""}
            iconPrefix={<LockOutlinedIcon className="text-title" />}
            value={formData.password}
            name="password"
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            error={!!errors.password}
            errorMessage={errors.password}
          />
        </div>
        
        <div className="flex items-center justify-between w-full mt-6 mb-6">
          <label className="flex items-center space-x-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={handleCheckboxChange}
              className="w-4 h-4 transition-all rounded form-checkbox accent-primary border-primary"
            />
            <span className="text-sm transition-colors text-title font-montserrat_regular group-hover:text-primary">
              Garde-moi connecté
            </span>
          </label>
          <p
            onClick={() => navigation("/forget-password")}
            className="text-sm transition-all cursor-pointer text-primary font-montserrat_medium hover:underline"
          >
            Mot de passe oublié ?
          </p>
        </div>
        
        <CustomButton
          text="Se connecter"
          className="w-full mt-2 transition-all duration-300 h-14 hover:shadow-lg"
          onClick={() => handleSubmit()}
        />
        
        <div className="mt-6 text-center">
          <span className="text-sm text-title font-montserrat_regular">
            Vous n'avez pas de compte ?{" "}
          </span>
          <span
            onClick={() => navigation("/role")}
            className="text-sm transition-all cursor-pointer text-primary font-montserrat_semi_bold hover:underline"
          >
            Créer un compte
          </span>
        </div>
      </form>
    </AuthLayout>
  );
};

export default Login;
